(function () {
  function initFBT(root) {
    if (!root || root.dataset.fbtInit === "1") return;
    root.dataset.fbtInit = "1";

    const moneyFormat = root.dataset.moneyFormat || "{{amount}}";
    const currency = root.dataset.currency || "GBP";

    const items = Array.from(root.querySelectorAll("[data-fbt-item]"));
    const totalEl = root.querySelector("[data-fbt-total]");
    const totalCompareEl = root.querySelector("[data-fbt-total-compare]");
    const addBtn = root.querySelector("[data-fbt-add]");
    const msgEl = root.querySelector("[data-fbt-message]");

    let addInFlight = false;
    let lastAddClickAt = 0;
    let pendingNavigationUrl = "";
    const ADD_DEBOUNCE_MS = 600;

    function setMessage(text) {
      if (msgEl) msgEl.textContent = text || "";
    }

    // ------------------------------------------------------------
    // Hold normal link navigation while FBT is adding items
    // ------------------------------------------------------------
    function onDocumentClickDuringAdd(e) {
      if (!addInFlight) return;
      if (!e || e.defaultPrevented) return;

      // Allow modified clicks/new tabs to behave normally.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      const clickTarget = e.target && e.target.closest
        ? e.target
        : e.target && e.target.parentElement;

      const link = clickTarget && clickTarget.closest
        ? clickTarget.closest("a[href]")
        : null;

      if (!link) return;

      const rawHref = String(link.getAttribute("href") || "").trim();
      if (!rawHref || rawHref === "#" || rawHref.startsWith("#")) return;
      if (link.hasAttribute("download")) return;

      const target = String(link.getAttribute("target") || "").trim().toLowerCase();
      if (target && target !== "_self") return;

      let url;

      try {
        url = new URL(link.href, window.location.href);
      } catch (err) {
        return;
      }

      if (!/^https?:$/.test(url.protocol)) return;

      pendingNavigationUrl = url.href;

      e.preventDefault();
      e.stopPropagation();

      if (typeof e.stopImmediatePropagation === "function") {
        e.stopImmediatePropagation();
      }
    }

    document.addEventListener("click", onDocumentClickDuringAdd, true);

    // ------------------------------------------------------------
    // Abort only if the Shopify section unloads
    // ------------------------------------------------------------
    const aborter = new AbortController();
    const signal = aborter.signal;

    const sectionEl = root.closest('[id^="shopify-section-"]');
    const sectionId = sectionEl ? sectionEl.id.replace("shopify-section-", "") : null;

    function cleanupAndAbort() {
      try { aborter.abort(); } catch (e) {}

      document.removeEventListener("shopify:section:unload", onSectionUnload);
      document.removeEventListener("click", onDocumentClickDuringAdd, true);
    }

    function onSectionUnload(e) {
      const unloaded = e && e.detail && e.detail.sectionId ? String(e.detail.sectionId) : null;

      if (!sectionId || unloaded === sectionId) {
        cleanupAndAbort();
      }
    }

    document.addEventListener("shopify:section:unload", onSectionUnload);

    async function safeFetch(url, options) {
      const opts = Object.assign({}, options || {});
      opts.signal = signal;
      return await fetch(url, opts);
    }

    function formatMoney(cents) {
      try {
        if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
          return window.Shopify.formatMoney(cents, moneyFormat);
        }
      } catch (e) {}

      const value = Number(cents || 0) / 100;

      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-GB", {
          style: "currency",
          currency
        }).format(value);
      } catch (e) {
        return "£" + value.toFixed(2);
      }
    }

    function parsePositiveInt(raw) {
      const n = parseInt(String(raw || "").trim(), 10);
      return Number.isFinite(n) && n > 0 ? n : null;
    }

    function parsePriceBreaks(raw) {
      if (!raw) return [];

      try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];

        return parsed
          .map((b) => ({
            min: parsePositiveInt(b.min),
            price: Number(b.price || 0)
          }))
          .filter((b) => b.min && Number.isFinite(b.price) && b.price > 0)
          .sort((a, b) => a.min - b.min);
      } catch (e) {
        return [];
      }
    }

    function getUnitPriceForQty(data, qty) {
      if (!data) return 0;

      let unitPrice = Number(data.price || 0);
      const breaks = Array.isArray(data.priceBreaks) ? data.priceBreaks : [];

      breaks.forEach((priceBreak) => {
        if (qty >= priceBreak.min) {
          unitPrice = Number(priceBreak.price || unitPrice);
        }
      });

      return unitPrice;
    }

    function getVariantPayload(item) {
      const selector = item.querySelector("[data-fbt-variant-select]");
      if (!selector) return null;

      const read = (node) => ({
        id: Number(node.value || 0),
        price: Number(node.dataset.price || 0),
        compare: Number(node.dataset.compare || 0),
        available: node.dataset.available === "true",
        img: node.dataset.img || "",
        imgset: node.dataset.imgset || "",
        priceBreaks: parsePriceBreaks(node.dataset.priceBreaks || "")
      });

      if (selector.tagName === "SELECT") {
        const opt = (selector.selectedOptions && selector.selectedOptions[0]) || selector.options[selector.selectedIndex];
        return opt ? read(opt) : null;
      }

      return read(selector);
    }

    function getQtyInput(item) {
      return item.querySelector("[data-fbt-qty]");
    }

    // Totals: allow blank while typing and use last valid value.
    function getQtyForTotals(item) {
      const input = getQtyInput(item);
      if (!input) return 1;

      const raw = String(input.value || "").trim();

      if (raw === "") {
        const last = parseInt(input.dataset.fbtLastValid || "1", 10);
        return Number.isFinite(last) && last > 0 ? last : 1;
      }

      const n = parsePositiveInt(raw) || 1;
      input.dataset.fbtLastValid = String(n);
      return n;
    }

    // Add: blank quantity becomes 1.
    function getQtyForAdd(item) {
      const input = getQtyInput(item);
      if (!input) return 1;

      const raw = String(input.value || "").trim();
      if (raw === "") return 1;

      const n = parsePositiveInt(raw) || 1;
      input.dataset.fbtLastValid = String(n);
      return n;
    }

    function updateItemUI(item, qty) {
      const data = getVariantPayload(item);
      if (!data || !Number.isFinite(data.id) || data.id <= 0) return;

      const safeQty = parsePositiveInt(qty) || getQtyForTotals(item);
      const unitPrice = getUnitPriceForQty(data, safeQty);

      const currentEl = item.querySelector("[data-fbt-price-current]");
      const compareEl = item.querySelector("[data-fbt-price-compare]");

      if (currentEl) currentEl.textContent = formatMoney(unitPrice);

      if (compareEl) {
        const hasCompare = data.compare && data.compare > unitPrice;
        compareEl.hidden = !hasCompare;
        compareEl.textContent = hasCompare ? formatMoney(data.compare) : "";
      }

      const img = item.querySelector(".fbt-card__img");
      if (img && data.img) {
        img.src = data.img;
        if (data.imgset) img.srcset = data.imgset;
      }

      // Extras only: reflect availability on checkbox.
      if (item.dataset.fbtExtra === "1") {
        const check = item.querySelector("[data-fbt-check]");
        if (check) {
          if (data.available) {
            if (check.disabled) check.disabled = false;
          } else {
            check.checked = false;
            check.disabled = true;
          }
        }
      }
    }

    function recalcTotal() {
      let total = 0;
      let totalCompare = 0;

      items.forEach((item) => {
        const qty = getQtyForTotals(item);
        const data = getVariantPayload(item);

        updateItemUI(item, qty);

        const check = item.querySelector("[data-fbt-check]");
        const included = check ? check.checked : true;
        if (!included) return;

        if (!data || !Number.isFinite(data.id) || data.id <= 0) return;

        const unitPrice = getUnitPriceForQty(data, qty);
        total += unitPrice * qty;

        const compareUnit = data.compare && data.compare > unitPrice ? data.compare : unitPrice;
        totalCompare += compareUnit * qty;
      });

      if (totalEl) totalEl.textContent = formatMoney(total);

      if (totalCompareEl) {
        const show = totalCompare > total;
        totalCompareEl.hidden = !show;
        totalCompareEl.textContent = show ? formatMoney(totalCompare) : "";
      }
    }

    async function fetchCart() {
      const res = await safeFetch("/cart.js", {
        headers: { "Accept": "application/json" }
      });

      return await res.json();
    }

    function getCartQtyForVariant(cart, variantId) {
      if (!cart || !Array.isArray(cart.items)) return 0;

      const line = cart.items.find((i) => Number(i.variant_id) === Number(variantId));
      return line ? Number(line.quantity || 0) : 0;
    }

    async function refreshCartForTheme(fallbackCart) {
      const cartItemsComponent = document.querySelector("cart-items-component");

      if (cartItemsComponent) {
        try {
          await customElements.whenDefined("cart-items-component");

          if (typeof cartItemsComponent.fetchCartData === "function") {
            return await cartItemsComponent.fetchCartData();
          }
        } catch (e) {
          if (e && (e.name === "AbortError" || String(e).includes("AbortError"))) throw e;
        }
      }

      return fallbackCart || fetchCart();
    }

    async function dispatchCartUpdate(cart, addedLines) {
      const lines = Array.isArray(addedLines)
        ? addedLines.map((line) => ({
            merchandiseId: String(line.variantId),
            quantity: Number(line.qty || 1)
          }))
        : [];

      try {
        const mod = await import("@shopify/events");
        const CartLinesUpdateEvent = mod && mod.CartLinesUpdateEvent;

        if (CartLinesUpdateEvent) {
          const deferredEventPromise = CartLinesUpdateEvent.createPromise
            ? CartLinesUpdateEvent.createPromise()
            : null;

          const eventInit = {
            action: "add",
            context: "product",
            lines,
            promise: deferredEventPromise ? deferredEventPromise.promise : Promise.resolve({
              cart,
              detail: {
                items: cart && cart.items ? cart.items : [],
                source: "fbt",
                itemCount: cart && cart.item_count ? cart.item_count : 0,
                didError: false
              }
            })
          };

          document.dispatchEvent(new CartLinesUpdateEvent(eventInit));

          const refreshedCart = await refreshCartForTheme(cart);
          const eventCart = CartLinesUpdateEvent.createCartFromAjaxResponse
            ? CartLinesUpdateEvent.createCartFromAjaxResponse(refreshedCart)
            : refreshedCart;

          if (deferredEventPromise) {
            deferredEventPromise.resolve({
              cart: eventCart,
              detail: {
                items: refreshedCart && refreshedCart.items ? refreshedCart.items : [],
                source: "fbt",
                itemCount: refreshedCart && refreshedCart.item_count ? refreshedCart.item_count : 0,
                sections: {},
                didError: false
              }
            });
          }

          return refreshedCart;
        }
      } catch (e) {
        if (e && (e.name === "AbortError" || String(e).includes("AbortError"))) throw e;
      }

      document.dispatchEvent(new CustomEvent("cart:update", {
        bubbles: true,
        detail: { data: { itemCount: cart.item_count, source: "fbt" } }
      }));

      document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));

      return refreshCartForTheme(cart);
    }

    function getCartDrawerComponent() {
      return document.querySelector("cart-drawer-component") || document.querySelector("cart-drawer");
    }

    function getThemeDrawer(drawerComponent) {
      return (drawerComponent && drawerComponent.closest && drawerComponent.closest("theme-drawer")) ||
        document.querySelector("#cart-drawer") ||
        document.querySelector("theme-drawer");
    }

    function drawerAutoOpenEnabled(drawerComponent) {
      if (!drawerComponent) return false;
      if (drawerComponent.hasAttribute("auto-open")) return true;

      const d = drawerComponent.dataset || {};
      return d.autoOpen === "true" || d.autoOpen === "1";
    }

    function openCartUI() {
      const drawerComponent = getCartDrawerComponent();
      const themeDrawer = getThemeDrawer(drawerComponent);

      if (drawerComponent && drawerAutoOpenEnabled(drawerComponent) && themeDrawer) {
        try {
          if (typeof themeDrawer.open === "function") {
            themeDrawer.open();
            return;
          }

          if ("open" in themeDrawer) {
            themeDrawer.open = true;
            return;
          }

          themeDrawer.setAttribute("open", "");
          return;
        } catch (e) {}
      }

      if (drawerComponent && drawerAutoOpenEnabled(drawerComponent)) {
        try {
          if (typeof drawerComponent.open === "function") {
            drawerComponent.open();
            return;
          }

          if ("open" in drawerComponent) {
            drawerComponent.open = true;
            return;
          }

          drawerComponent.setAttribute("open", "");
          return;
        } catch (e) {}
      }

      const notif = document.querySelector("cart-notification") || document.querySelector("cart-notification-component");

      if (notif) {
        try {
          if (typeof notif.open === "function") {
            notif.open();
            return;
          }

          if ("open" in notif) {
            notif.open = true;
            return;
          }
        } catch (e) {}
      }
    }

    async function attemptAdd(variantId, qty) {
      let text = "";
      let json = null;

      const res = await safeFetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: qty }]
        })
      });

      try { text = await res.text(); } catch (e) {}
      try { json = text ? JSON.parse(text) : null; } catch (e) {}

      if (res.ok) return { ok: true, status: res.status, message: "" };

      const msg =
        (json && (json.description || json.message)) ||
        (typeof text === "string" ? text : "") ||
        "HTTP " + res.status;

      return { ok: false, status: res.status, message: msg };
    }

    // Step-down + refine: find max quantity Shopify will accept for this add.
    async function addBestEffort(variantId, desiredQty) {
      let r = await attemptAdd(variantId, desiredQty);
      if (r.ok) return { attempted: desiredQty, ok: true, lastError: "" };

      const lastError = r.message || "Add failed";

      let q = Math.max(1, Math.floor(desiredQty / 2));
      let best = 0;

      for (let i = 0; i < 10 && q >= 1; i++) {
        r = await attemptAdd(variantId, q);

        if (r.ok) {
          best = q;
          break;
        }

        q = Math.floor(q / 2);
      }

      if (best === 0) {
        r = await attemptAdd(variantId, 1);

        return r.ok
          ? { attempted: 1, ok: true, lastError }
          : { attempted: 0, ok: false, lastError: r.message || lastError };
      }

      const hi0 = Math.min(desiredQty, best * 2);
      let lo = best + 1;
      let hi = hi0;
      let upperBest = best;

      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        r = await attemptAdd(variantId, mid);

        if (r.ok) {
          upperBest = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }

      return { attempted: upperBest, ok: true, lastError };
    }

    async function handleAddAll() {
      const now = Date.now();

      if (addInFlight) return;
      if (now - lastAddClickAt < ADD_DEBOUNCE_MS) return;

      lastAddClickAt = now;

      setMessage("");
      if (!addBtn) return;

      addInFlight = true;
      addBtn.disabled = true;
      addBtn.setAttribute("aria-busy", "true");

      try {
        const selected = [];

        items.forEach((item) => {
          const check = item.querySelector("[data-fbt-check]");
          const included = check ? check.checked : true;
          if (!included) return;

          const data = getVariantPayload(item);
          if (!data || !Number.isFinite(data.id) || data.id <= 0) return;

          selected.push({
            variantId: Number(data.id),
            qty: getQtyForAdd(item),
            title: item.dataset.fbtTitle || "Item"
          });
        });

        if (!selected.length) {
          setMessage("Please select at least one item.");
          return;
        }

        let cartState = await fetchCart();

        let anyAdded = false;
        let anyCapped = false;
        const capNotes = [];
        const successfullyAddedLines = [];

        for (const line of selected) {
          const beforeQty = getCartQtyForVariant(cartState, line.variantId);

          const firstTry = await attemptAdd(line.variantId, line.qty);

          let cartAfter = await fetchCart();
          let afterQty = getCartQtyForVariant(cartAfter, line.variantId);
          let delta = Math.max(0, afterQty - beforeQty);

          if (delta === 0 && !firstTry.ok) {
            await addBestEffort(line.variantId, line.qty);

            cartAfter = await fetchCart();
            afterQty = getCartQtyForVariant(cartAfter, line.variantId);
            delta = Math.max(0, afterQty - beforeQty);
          }

          cartState = cartAfter;

          if (delta > 0) {
            anyAdded = true;
            successfullyAddedLines.push({
              variantId: line.variantId,
              qty: delta,
              title: line.title
            });
          }

          if (delta < line.qty) {
            anyCapped = true;
            capNotes.push(`${line.title}: ${delta}/${line.qty}`);
          }
        }

        cartState = await dispatchCartUpdate(cartState, successfullyAddedLines);
        openCartUI();
        recalcTotal();

        if (!anyAdded) {
          setMessage("Nothing was added. Please check availability or cart limits.");
        } else if (anyCapped) {
          if (selected.length === 1) {
            const note = capNotes[0] || "";
            const actual = parseInt((note.split(":")[1] || "").split("/")[0], 10);

            setMessage(`Only ${Number.isFinite(actual) ? actual : "some"} items were added to your cart due to availability.`);
          } else {
            setMessage(`Only some items were added due to availability. (${capNotes.join(" • ")})`);
          }
        } else {
          setMessage("");
        }

      } catch (e) {
        if (e && (e.name === "AbortError" || String(e).includes("AbortError"))) return;

        console.error(e);
        setMessage("Something went wrong adding to cart. Please try again.");
      } finally {
        addInFlight = false;

        if (addBtn) {
          addBtn.disabled = false;
          addBtn.removeAttribute("aria-busy");
        }

        if (pendingNavigationUrl) {
          const url = pendingNavigationUrl;
          pendingNavigationUrl = "";

          window.location.assign(url);
        }
      }
    }

    // Qty UX: select "1" on focus, allow blank while typing, digits only.
    root.addEventListener("focusin", (e) => {
      const input = e.target.closest("[data-fbt-qty]");
      if (!input) return;

      const v = String(input.value || "").trim();

      if (v === "1" || v === "") {
        setTimeout(() => {
          try { input.select(); } catch (err) {}
        }, 0);
      }
    }, true);

    root.addEventListener("click", (e) => {
      const plus = e.target.closest("[data-fbt-qty-plus]");
      const minus = e.target.closest("[data-fbt-qty-minus]");

      if (!plus && !minus) return;

      const item = e.target.closest("[data-fbt-item]");
      if (!item) return;

      e.preventDefault();
      e.stopPropagation();

      const input = item.querySelector("[data-fbt-qty]");
      if (!input) return;

      const cur = parsePositiveInt(input.value) || 1;

      if (plus) input.value = String(cur + 1);
      if (minus) input.value = String(Math.max(1, cur - 1));

      input.dataset.fbtLastValid = input.value;
      recalcTotal();
    }, true);

    root.addEventListener("input", (e) => {
      const input = e.target.closest("[data-fbt-qty]");
      if (!input) return;

      const cleaned = String(input.value || "").replace(/[^\d]/g, "");
      input.value = cleaned;

      if (cleaned !== "") input.dataset.fbtLastValid = cleaned;

      recalcTotal();
      e.stopPropagation();
    }, true);

    root.addEventListener("change", (e) => {
      const sel = e.target.closest("[data-fbt-variant-select]");
      const chk = e.target.closest("[data-fbt-check]");

      if (!sel && !chk) return;

      recalcTotal();
    }, true);

    if (addBtn) addBtn.addEventListener("click", handleAddAll);

    items.forEach((item) => updateItemUI(item, getQtyForTotals(item)));
    recalcTotal();
  }

  function initAll() {
    document.querySelectorAll("[data-fbt-root]").forEach(initFBT);
  }

  initAll();
  document.addEventListener("DOMContentLoaded", initAll);
  document.addEventListener("shopify:section:load", initAll);
  document.addEventListener("shopify:section:reorder", initAll);
  document.addEventListener("shopify:section:select", initAll);
})();